import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { DirtyFieldChange } from '@/hooks'
import { Building } from '@/reducers/types'

import { FormChangeFields, getFormChanges } from '../../../shared/form-changes'
import {
  PlaceFeatureKey,
  PlaceFeatureTab,
  PlaceFlags,
} from '../../features/places-features.constants'
import { buildFlagsPayload, getFeaturesByTab, toFlags } from '../../features/places-features.rules'
import { usePlaceSave } from '../use-place-save'

interface UsePlaceFlagsFormParams {
  building: Building
  isAdmin: boolean
  onDirtyChange?: (isDirty: boolean, getChanges?: () => DirtyFieldChange[]) => void
  tab: PlaceFeatureTab
}

const pickFlags = (building: Building, keys: PlaceFeatureKey[]): PlaceFlags =>
  Object.fromEntries(keys.map((key) => [key, !!building[key]])) as PlaceFlags

export const usePlaceFlagsForm = ({
  building,
  isAdmin,
  onDirtyChange,
  tab,
}: UsePlaceFlagsFormParams) => {
  const { t } = useTranslation(['organisation'])
  const { isSaving, save } = usePlaceSave(building)
  const [errorFeatures, setErrorFeatures] = useState<string[]>([])
  const [errorMessage, setErrorMessage] = useState<string>()
  const [isEditing, setIsEditing] = useState(false)

  const keys = useMemo(
    () =>
      getFeaturesByTab(tab)
        .filter((feature) => !('hidden' in feature && feature.hidden))
        .map(({ key }) => key as PlaceFeatureKey),
    [tab],
  )

  const serverValues = useMemo(() => pickFlags(building, keys), [building, keys])

  const form = useForm<PlaceFlags>({
    defaultValues: serverValues,
    // Note: a background update of the building must not wipe what the user is editing
    resetOptions: { keepDirtyValues: true },
    values: serverValues,
  })

  const { isDirty } = form.formState

  const changeFields = useMemo<FormChangeFields<PlaceFlags>>(
    () =>
      Object.fromEntries(
        keys.map((key) => [
          key,
          {
            format: (value: unknown) =>
              value ? t('organisation:places.save.on') : t('organisation:places.save.off'),
            label: t(`organisation:places.building.toggles.${key}`),
          },
        ]),
      ),
    [keys, t],
  )

  const getChanges = useCallback(
    () =>
      getFormChanges({
        defaultValues: form.formState.defaultValues,
        fields: changeFields,
        values: form.getValues(),
      }),
    [changeFields, form],
  )

  useEffect(() => {
    const isDirtyForm = isEditing && isDirty
    onDirtyChange?.(isDirtyForm, isDirtyForm ? getChanges : undefined)
  }, [isEditing, isDirty, getChanges, onDirtyChange])

  const handleEdit = () => setIsEditing(true)

  const handleCancel = () => {
    form.reset(serverValues)
    setErrorFeatures([])
    setErrorMessage(undefined)
    setIsEditing(false)
  }

  const handleSave = form.handleSubmit(async (values) => {
    const payload = buildFlagsPayload({ isAdmin, next: values, server: building })
    const result = await save({ nextFlags: { ...toFlags(building), ...values }, payload })

    if (result.ok) {
      form.reset(pickFlags(result.building, keys))
      setErrorFeatures([])
      setErrorMessage(undefined)
      setIsEditing(false)
    } else if ('error' in result) {
      // Note: the backend names every flag of the provider, but only switches that are on can be the cause
      const enabled = result.error.features.filter((key) => values[key])
      setErrorFeatures(enabled.length ? enabled : result.error.features)
      setErrorMessage(result.message)
    }
  })

  return {
    errorFeatures,
    errorMessage,
    form,
    handleCancel,
    handleEdit,
    handleSave,
    isDirty,
    isEditing,
    isSaving,
    keys,
  }
}
